'use client'

import { Icon } from '@/components/ui/icon'
import { CONTACT_PATHS } from '@/config/contact'
import { useFeatureFlags } from '@/lib/features/feature-flags-context'
import { addSelectedService } from '@/lib/store/features/booking/bookingSlice'
import { RootState } from '@/lib/store/store'
import { ServiceItem } from '@/lib/types/operations/service'
import { showToast } from '@/lib/utils/toast'
import { Link, PressEvent } from '@heroui/react'
import { Button } from '@/components/ui/button'
import { useCallback, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { useDispatch, useSelector } from 'react-redux'

interface AddToReservationButtonProps {
  service: ServiceItem
  className?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'solid' | 'bordered' | 'light'
  showToastNotification?: boolean
  context?: 'card' | 'detail'
}

export function AddToReservationButton({
  service,
  className,
  size = 'md',
  variant = 'solid',
  showToastNotification = true,
  context = 'card'
}: AddToReservationButtonProps) {
  const dispatch = useDispatch()
  const selectedServiceIds = useSelector((state: RootState) => state.booking.persisted.selectedServiceIds)
  const isLoading = useSelector((state: RootState) => state.booking.current.isSubmitting)

  const router = useRouter()
  const [isNavPending, startNavTransition] = useTransition()
  const { bookingEnabled } = useFeatureFlags()

  const handleBookService = useCallback(() => {
    if (!service.id) return
    // A service is booked as an appointment via the /book wizard, which reads
    // booking.persisted.selectedServiceIds (seeded by the dispatch above).
    // /checkout is the PRODUCTS cart (separate `cart` slice) and would strand
    // this selection on an empty, auth-gated cart page.
    dispatch(addSelectedService(service.id))
    // Wrap the push in a transition so isNavPending stays true until /book
    // commits (~2s on staging); the CTA dims instead of sitting silent.
    startNavTransition(() => {
      router.push('/book')
    })
  }, [dispatch, service.id, router])

  const handleAddToAppointment = useCallback(
    (e: PressEvent) => {
      try {
        if (service.id) {
          dispatch(addSelectedService(service.id))
          if (showToastNotification) {
            showToast({
              title: 'Service added to appointment',
              color: 'success'
            })
          }
        } else {
          console.error('Service ID is undefined')
          if (showToastNotification) {
            showToast({
              title: 'Failed to add service with undefined ID. Please try again.',
              color: 'danger',
              timeout: 5000
            })
          }
        }
      } catch (error) {
        console.error('Failed to add service to appointment:', error)
        if (showToastNotification) {
          showToast({
            title: 'Failed to add service to appointment. Please try again.',
            color: 'danger',
            timeout: 5000
          })
        }
      }
    },
    [dispatch, service.id, showToastNotification]
  )

  const isSelected = service.id ? selectedServiceIds.includes(service.id) : false

  // Booking off: no appointment to add to. Detail pages get the quote form as
  // their one action; cards keep Learn More only.
  if (!bookingEnabled) {
    if (context === 'detail') {
      return (
        <div className={`${className} flex flex-col gap-3`}>
          <Button size={size} variant='primary' className='w-full font-medium' onPress={() => router.push(CONTACT_PATHS.quote)} data-testid='service-quote-cta'>
            <Icon icon='quote' size='lg' /> Get a Quote
          </Button>
        </div>
      )
    }
    return (
      <div className={className}>
        <div className='flex justify-between items-center'>
          <Link href={`/services/${service.id}`}>
            <Button size={size} variant='ghost'>
              <Icon icon='arrow-right' size='sm' /> Learn More
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  if (context === 'detail') {
    return (
      <div className={`${className} flex flex-col gap-3`}>
        <Button
          size={size}
          variant='primary'
          isDisabled={isNavPending}
          className='w-full font-medium'
          onPress={handleBookService}
        >
          <Icon icon='schedule' size='lg' /> {isNavPending ? 'Opening...' : 'Book This Service'}
        </Button>
        <Button
          size={size}
          variant='outline'
          isDisabled={isSelected}
          isLoading={isLoading}
          className='w-full font-medium'
          onPress={handleAddToAppointment}
          data-testid='add-to-appointment-cta'
        >
          {isSelected ? (
            <Icon icon='check-circle' size='lg' />
          ) : (
            <Icon icon='add-circle' size='lg' />
          )}
          {isSelected ? 'Added' : 'Add to Appointment'}
        </Button>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className='flex justify-between items-center'>
        <div>
          <Link href={`/services/${service.id}`}>
            <Button
              size={size}
              variant='ghost'
            >
              <Icon icon='arrow-right' size='sm' /> Learn More
            </Button>
          </Link>
        </div>
        <Button
          size={size}
          variant={variant}
          isDisabled={isSelected}
          isLoading={isLoading}
          onPress={handleAddToAppointment}
        >
          {isSelected ? (
            <Icon icon='check-circle' size='lg' />
          ) : (
            <Icon icon='add-circle' size='lg' />
          )}
          {isSelected ? 'Added' : 'Add to Appointment'}
        </Button>
      </div>
    </div>
  )
}
